import { rest } from "msw";
import type { FacilityAdapterType } from "../adapters/FacilityAdapters.ts";
import type { DomainConfig } from "../config.ts";
import { AttributeDataType } from "../schemas/AttributeSchemas.ts";
import {
  FacilitySchema,
  FacilityStatus,
  FacilityTypes,
  type FacilityType,
} from "../schemas/FacilitySchemas.ts";
import {
  getApiUrlWithEndpoint,
  type BuildMockHandlersType,
} from "./handlers.ts";
import { DomainFaker, DomainMock } from "./utils.ts";

export const FacilityMock = new DomainMock<FacilityType, typeof FacilitySchema>(
  FacilitySchema,
  () => {
    const createdAt = DomainFaker.en.date.past();

    return {
      id: DomainFaker.en.string.uuid(),
      name: DomainFaker.en.string.alpha(10),
      type: DomainFaker.en.helpers.arrayElement(Object.values(FacilityTypes)),
      status: DomainFaker.en.helpers.arrayElement([
        FacilityStatus.ACTIVE,
        FacilityStatus.INACTIVE,
      ]),
      latitude: DomainFaker.en.number.int({ min: -90, max: 90 }),
      longitude: DomainFaker.en.number.int({ min: -180, max: 180 }),
      custom_attributes: {
        [DomainFaker.en.string.alpha(10)]: DomainFaker.en.string.alpha(10),
      },
      company_id: DomainFaker.en.string.uuid(),
      field_name: DomainFaker.en.string.alpha(10),
      data_type: DomainFaker.en.helpers.arrayElement(
        Object.values(AttributeDataType)
      ),
      created_at: createdAt.toISOString(),
      created_by: DomainFaker.en.string.uuid(),
      updated_at: DomainFaker.en.date
        .between({ from: createdAt, to: new Date() })
        .toISOString(),
      updated_by: DomainFaker.en.string.uuid(),
    };
  },
  true
).fix();

export const buildFacilityHandlers: BuildMockHandlersType<
  FacilityAdapterType
> = (configData: DomainConfig) => {
  const { nodeURL: url } = configData;

  return {
    getList: rest.post(
      getApiUrlWithEndpoint({
        url,
        version: 1,
        endpoint: `/facilities/search`,
      }),
      (req, res, ctx) => {
        const page = Number(req.url.searchParams.get("page"));
        const pageSize = Number(req.url.searchParams.get("pageSize"));
        const data = new Array(5).fill(0).map(() => FacilityMock.generate());

        return res(
          ctx.status(200),
          ctx.json({
            data,
            page_number: page,
            page_size: pageSize,
            total_entries: data.length,
            total_pages: 1,
          })
        );
      }
    ),
    getOne: rest.get(
      getApiUrlWithEndpoint({
        url,
        version: 1,
        endpoint: "/facilities/:id",
      }),
      (req, res, ctx) => {
        const { id } = req.params;

        if (typeof id !== "string") {
          return res(ctx.status(400));
        }

        const mockData = {
          ...FacilityMock.mock,
          id,
        };

        return res(ctx.status(200), ctx.json(mockData));
      }
    ),
  };
};
